import {Select, Stack, Text} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import PlateSchemaSelector from 'samples/PlateSchemaSelector';
import {PlateSchema, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import getPresetPlateSizes from 'settings/samples/getPresetPlateSizes';

function plateSizeValue(height: number, width: number): string {
  return `${height}x${width}`;
}

function plateSizeLabel(height: number, width: number): string {
  return `${height} x ${width} (${height * width} wells)`;
}

export default function PlateSizeEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const options = useMemo(
    () =>
      getPresetPlateSizes().map(([height, width]) => ({
        value: plateSizeValue(height, width),
        label: plateSizeLabel(height, width),
      })),
    []
  );

  const plateHeight = typeof config.plateHeight === 'number' ? config.plateHeight : null;
  const plateWidth = typeof config.plateWidth === 'number' ? config.plateWidth : null;
  const inheritsPlateDesign = typeof config.plateDesignTemplateId === 'string';
  const plateSize =
    plateHeight != null && plateWidth != null ? plateSizeValue(plateHeight, plateWidth) : null;
  const selectedPlateSchemaId = typeof config.plateSchemaId === 'string' ? config.plateSchemaId : null;
  const parsedPlateSize = useMemo(
    (): [number, number] | null =>
      plateHeight != null && plateWidth != null ? [plateHeight, plateWidth] : null,
    [plateHeight, plateWidth]
  );

  const plateSchemaFilter = useCallback(
    (schema: PlateSchema): boolean =>
      parsedPlateSize != null && schema.height === parsedPlateSize[0] && schema.width === parsedPlateSize[1],
    [parsedPlateSize]
  );

  if (inheritsPlateDesign) {
    return (
      <Text c="dimmed" fz="sm">
        Plate size and schema are inherited from the selected plate design template.
      </Text>
    );
  }

  return (
    <Stack spacing="xs">
      <Select
        placeholder="Select size..."
        data={options}
        value={plateSize}
        searchable={true}
        clearable={false}
        w="100%"
        disabled={disabled}
        onChange={(value) => {
          if (value != null) {
            const [heightText, widthText] = value.split('x');
            const height = Number(heightText);
            const width = Number(widthText);
            if (!Number.isInteger(height) || !Number.isInteger(width)) {
              return;
            }
            let updatedConfig = setValueInConfig(config, ['plateHeight'], height);
            updatedConfig = setValueInConfig(updatedConfig, ['plateWidth'], width);
            // Plate schema compatibility depends on size; clear stale selection on size change.
            updatedConfig = setValueInConfig(updatedConfig, ['plateSchemaId'], null);
            onChangeConfig(updatedConfig, ['plateHeight'], height);
          }
        }}
      />
      <Stack spacing={4}>
        <Text fz="sm" fw={600}>
          Plate Schema
        </Text>
        <PlateSchemaSelector
          plateSchemaType={PlateSchemaType.FIXED_PLATE}
          plateSchemaFilter={parsedPlateSize == null ? undefined : plateSchemaFilter}
          onChange={(schema) => {
            onChangeConfig(config, ['plateSchemaId'], schema?.id ?? null);
          }}
          registryId={undefined}
          selectedPlateSchemaId={selectedPlateSchemaId}
          disabled={disabled || parsedPlateSize == null}
          disabledMessage={parsedPlateSize == null ? 'Select a plate size first' : undefined}
          shouldShowNoSchemaOption={true}
        />
      </Stack>
    </Stack>
  );
}
